import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  QrCode,
  LayoutDashboard,
  PlusCircle,
  BarChart3,
  CreditCard,
  Users,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/create", label: "Create QR", icon: PlusCircle },
    { path: "/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/subscription", label: "Plans", icon: CreditCard },
    ...(user?.is_superuser ? [{ path: "/admin", label: "Admin", icon: Users }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0047FF]">
          <QrCode className="h-5 w-5 text-white" strokeWidth={2} />
        </div>
        <span className="text-lg font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          SKA QR
        </span>
      </div>

      <nav className="flex-1 px-3 py-4">
        <div className="space-y-1">
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <button
                key={path}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => {
                  navigate(path);
                  setMobileOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-[#0047FF]/10 text-[#0047FF]"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                {label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="border-t border-zinc-200 p-4">
        {user && (
          <div className="mb-3 flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-zinc-100 text-xs font-bold text-zinc-600">
                {user.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-bold text-zinc-950">{user.name}</p>
              <Badge
                variant="secondary"
                className="mt-0.5 text-[10px] uppercase tracking-wider"
                data-testid="user-plan-badge"
              >
                {user.plan || "free"}
              </Badge>
            </div>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-950"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          Sign out
        </button>
        <div className="mt-3 px-3 text-[10px] text-zinc-400 flex items-center justify-between">
          <a
            href="https://www.skatechnologies.com"
            target="_blank"
            rel="noreferrer"
            className="hover:text-zinc-600 transition-colors"
          >
            SKA Technologies
          </a>
          <span>v1.0.2</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        data-testid="mobile-menu-toggle"
        className="fixed left-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 bg-white shadow-sm lg:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 h-full w-64 border-r border-zinc-200 bg-white transition-transform duration-300 lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-zinc-200 bg-white lg:block">
        <SidebarContent />
      </aside>
    </>
  );
}
